/**
 * TemplateService
 *
 * This service handles all workflow template-related operations in the application.
 * It provides methods for managing workflow templates, including creation, retrieval,
 * updating, step management, preview, and applying templates to projects.
 *
 * @class
 */

import {
  pathWorkflowTemplates,
  pathWorkflowTemplatesApply,
} from '@frontend/__settings__/septSoftwarePaths';
import HttpService from './HttpService';
import {
  WorkflowTemplatePayload,
  WorkflowTemplateResponseData,
  WorkflowTemplateApplyPayload,
  WorkflowTemplateApplyResponseData,
  WorkflowGenericResponse,
} from '@frontend/types/workflows';

const http = new HttpService();

/**
 * Filters for listing templates
 */
export interface TemplateListFilters {
  page?: number;
  limit?: number;
  clientId?: string;
  ownerId?: string;
  search?: string;
}

/**
 * Response structure for template list endpoint
 */
export interface TemplateListResponse {
  templates: Array<{
    templateId: string;
    name: string;
    category: string;
    description?: string | null;
    client?: string | null;
    owner?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    metadata?: Record<string, unknown>;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    clientId?: string | null;
    ownerId?: string | null;
    search?: string | null;
  };
  sept_tenantid: string;
}

/**
 * Payload for creating a template step
 */
export interface CreateStepPayload {
  name: string;
  stepOrder?: number;
  discipline?: string;
  role?: string;
  category?: string;
  offsetType?: string;
  offsetDays?: number;
  durationDays?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Payload for updating a template step
 */
export interface UpdateStepPayload {
  name?: string;
  stepOrder?: number;
  discipline?: string;
  role?: string;
  category?: string;
  offsetType?: string;
  offsetDays?: number;
  durationDays?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Payload for reordering steps
 */
export interface ReorderStepsPayload {
  stepIds: string[];
}

/**
 * Payload for previewing a template
 */
export interface PreviewTemplatePayload {
  anchors: {
    projectStart?: string;
    constructionDate?: string;
    ifcDate?: string;
    [key: string]: string | undefined;
  };
}

/**
 * Response structure for template preview
 */
export interface PreviewTemplateResponse {
  timeline: Array<{
    stepId: string;
    stepName?: string;
    name?: string;
    plannedDate: string;
    offsetType: string;
    offsetDays: number;
    stepOrder?: number;
    discipline?: string | null;
    category?: string | null;
    durationDays?: number | null;
    endDate?: string | null;
    anchorType?: string | null;
  }>;
  warnings: string[];
  sept_tenantid: string;
  templateId?: string;
  templateName?: string;
  anchors?: {
    projectStart?: string;
    constructionDate?: string;
    ifcDate?: string;
  };
}

/**
 * Response structure for step operations
 */
export interface StepResponse {
  step: {
    stepId: string;
    templateId: string;
    stepOrder: number;
    name: string;
    discipline?: string | null;
    role?: string | null;
    category?: string | null;
    offsetType: string;
    offsetDays: number;
    durationDays?: number | null;
    metadata?: Record<string, unknown>;
    createdAt?: string | null;
  };
  sept_tenantid: string;
}

class TemplateService {
  /**
   * Retrieves a list of workflow templates with optional filtering and pagination
   *
   * @param {TemplateListFilters} filters - Filter and pagination options
   * @returns {Promise<TemplateListResponse>} List of templates with pagination metadata
   * @throws {Error} If the API request fails
   */
  static async listTemplates(
    filters?: TemplateListFilters
  ): Promise<TemplateListResponse> {
    const params = new URLSearchParams();
    
    if (filters?.page) {
      params.append('page', filters.page.toString());
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.clientId) {
      params.append('clientId', filters.clientId);
    }
    if (filters?.ownerId) {
      params.append('ownerId', filters.ownerId);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }

    const url = params.toString()
      ? `${pathWorkflowTemplates}?${params.toString()}`
      : pathWorkflowTemplates;

    const response = await http.get<WorkflowGenericResponse<TemplateListResponse>>(url);
    
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching templates.');
    }

    return response.data;
  }

  /**
   * Retrieves a single workflow template with all its steps
   *
   * @param {string} templateId - ID of the template
   * @returns {Promise<WorkflowTemplateResponseData>} Complete template data with steps
   * @throws {Error} If the API request fails
   */
  static async getTemplate(
    templateId: string
  ): Promise<WorkflowTemplateResponseData> {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    const response = await http.get<WorkflowGenericResponse<WorkflowTemplateResponseData>>(
      `${pathWorkflowTemplates}/${templateId}`
    );

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching the template.');
    }

    return response.data;
  }

  /**
   * Creates a new workflow template with steps
   *
   * @param {WorkflowTemplatePayload} payload - Template data including steps
   * @returns {Promise<WorkflowTemplateResponseData>} Created template with steps
   * @throws {Error} If the API request fails
   */
  static async createTemplate(
    payload: WorkflowTemplatePayload
  ): Promise<WorkflowTemplateResponseData> {
    const response = await http.post<
      WorkflowGenericResponse<WorkflowTemplateResponseData>,
      WorkflowTemplatePayload
    >(pathWorkflowTemplates, payload);

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while creating the template.');
    }

    return response.data;
  }

  /**
   * Updates an existing workflow template's metadata
   *
   * @param {string} templateId - ID of the template
   * @param {Partial<WorkflowTemplatePayload>} payload - Fields to update
   * @returns {Promise<WorkflowTemplateResponseData>} Updated template data
   * @throws {Error} If the API request fails
   */
  static async updateTemplate(
    templateId: string,
    payload: Partial<WorkflowTemplatePayload>
  ): Promise<WorkflowTemplateResponseData> {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    const response = await http.put<
      WorkflowGenericResponse<WorkflowTemplateResponseData>,
      Partial<WorkflowTemplatePayload>
    >(`${pathWorkflowTemplates}/${templateId}`, payload);

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while updating the template.');
    }

    return response.data;
  }

  /**
   * Deletes a workflow template (optional - not implemented in backend yet)
   *
   * @param {string} templateId - ID of the template to delete
   * @returns {Promise<{ message: string }>} Success message
   * @throws {Error} If the API request fails
   */
  static async deleteTemplate(
    templateId: string
  ): Promise<{ message: string }> {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    // Note: DELETE endpoint may not be implemented yet
    // This is a placeholder for future implementation
    await http.delete<WorkflowGenericResponse<{ message: string }>>(
      `${pathWorkflowTemplates}/${templateId}`
    );

    return {
      message: 'Template deleted successfully',
    };
  }

  /**
   * Creates a new step for a workflow template
   *
   * @param {string} templateId - ID of the template
   * @param {CreateStepPayload} payload - Step data
   * @returns {Promise<StepResponse>} Created step data
   * @throws {Error} If the API request fails
   */
  static async createStep(
    templateId: string,
    payload: CreateStepPayload
  ): Promise<StepResponse> {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    const response = await http.post<
      WorkflowGenericResponse<StepResponse>,
      CreateStepPayload
    >(`${pathWorkflowTemplates}/${templateId}/steps`, payload);

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while creating the step.');
    }

    return response.data;
  }

  /**
   * Updates an existing step in a workflow template
   *
   * @param {string} templateId - ID of the template
   * @param {string} stepId - ID of the step
   * @param {UpdateStepPayload} payload - Fields to update
   * @returns {Promise<StepResponse>} Updated step data
   * @throws {Error} If the API request fails
   */
  static async updateStep(
    templateId: string,
    stepId: string,
    payload: UpdateStepPayload
  ): Promise<StepResponse> {
    if (!templateId || !stepId) {
      throw new Error('Template ID and Step ID are required');
    }

    const response = await http.put<
      WorkflowGenericResponse<StepResponse>,
      UpdateStepPayload
    >(`${pathWorkflowTemplates}/${templateId}/steps/${stepId}`, payload);

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while updating the step.');
    }

    return response.data;
  }

  /**
   * Deletes a step from a workflow template
   *
   * @param {string} templateId - ID of the template
   * @param {string} stepId - ID of the step to delete
   * @returns {Promise<{ message: string }>} Success message
   * @throws {Error} If the API request fails
   */
  static async deleteStep(
    templateId: string,
    stepId: string
  ): Promise<{ message: string }> {
    if (!templateId || !stepId) {
      throw new Error('Template ID and Step ID are required');
    }

    const response = await http.delete<WorkflowGenericResponse<{ message: string; stepId: string; templateId: string }>>(
      `${pathWorkflowTemplates}/${templateId}/steps/${stepId}`
    );

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while deleting the step.');
    }

    return {
      message: response.data.message || 'Step deleted successfully',
    };
  }

  /**
   * Reorders steps in a workflow template
   *
   * @param {string} templateId - ID of the template
   * @param {string[]} orderedStepIds - Ordered array of step IDs
   * @returns {Promise<{ message: string; orderedStepIds: string[] }>} Success message with ordered IDs
   * @throws {Error} If the API request fails
   */
  static async reorderSteps(
    templateId: string,
    orderedStepIds: string[]
  ): Promise<{ message: string; orderedStepIds: string[] }> {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    if (!orderedStepIds || !Array.isArray(orderedStepIds) || orderedStepIds.length === 0) {
      throw new Error('Ordered list of step IDs is required');
    }

    const payload: ReorderStepsPayload = {
      stepIds: orderedStepIds,
    };

    const response = await http.patch<
      WorkflowGenericResponse<{ message: string; templateId: string; orderedStepIds: string[] }>,
      ReorderStepsPayload
    >(`${pathWorkflowTemplates}/${templateId}/steps/reorder`, payload);

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while reordering steps.');
    }

    return {
      message: response.data.message || 'Steps reordered successfully',
      orderedStepIds: response.data.orderedStepIds,
    };
  }

  /**
   * Previews a workflow template timeline with sample anchor dates
   *
   * @param {string} templateId - ID of the template
   * @param {PreviewTemplatePayload['anchors']} anchors - Anchor dates for calculation
   * @returns {Promise<PreviewTemplateResponse>} Timeline preview with warnings
   * @throws {Error} If the API request fails
   */
  static async previewTemplate(
    templateId: string,
    anchors: PreviewTemplatePayload['anchors']
  ): Promise<PreviewTemplateResponse> {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    const payload: PreviewTemplatePayload = {
      anchors,
    };

    const response = await http.post<
      WorkflowGenericResponse<PreviewTemplateResponse>,
      PreviewTemplatePayload
    >(`${pathWorkflowTemplates}/${templateId}/preview`, payload);

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while previewing the template.');
    }

    const normalizedTimeline = (response.data.timeline || []).map((item) => ({
      ...item,
      stepName: item.stepName || (item as any).name || 'Untitled Step',
    }));

    return {
      ...response.data,
      timeline: normalizedTimeline,
    };
  }

  /**
   * Applies a workflow template to a project, creating milestones
   *
   * @param {WorkflowTemplateApplyPayload} payload - Template application data
   * @returns {Promise<WorkflowTemplateApplyResponseData>} Created instance and milestones
   * @throws {Error} If the API request fails
   */
  static async applyTemplate(
    payload: WorkflowTemplateApplyPayload
  ): Promise<WorkflowTemplateApplyResponseData> {
    const response = await http.post<
      WorkflowGenericResponse<WorkflowTemplateApplyResponseData>,
      WorkflowTemplateApplyPayload
    >(pathWorkflowTemplatesApply, payload);

    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while applying the template.');
    }

    return response.data;
  }
}

export default TemplateService;
